import React from 'react';
import styled from 'styled-components';
import { COLORS, SHADOWS } from '../../app/common/constants/styles';
import { TranslationHelpers } from '../../i18n/translationHelpers';
import { IRewardsBalanceResponse, IRewardsBalanceState } from './rewardsClub.interface';
import { RewardsTransactionsHistory } from './rewardsTransactionHistory/RewardsTransactionsHistory';
import ExpiringPointsList, { NoTermDetailsFound } from './expiringPointsList/ExpiringPointsList';
import { RewardsBanner } from './rewardsBalance/RewardsBanner';
import Dropdown from '../../app/common/components/Dropdown';
import { rewardsBalanceActions } from './rewardsBalance/redux/rewardsBalanceActions';
import { useDispatch, useSelector } from 'react-redux';
import { IReduxRootState } from '../../app/reduxRootState.interface';
import { createCurrencyDropdownOptions } from './currencyDropdown/CurrencyDropdownHelper';
import * as CurrencySelectorStyles from './currencyDropdown/CurrencySelectorStyles';
import { formatCashbackBalanceByLocale } from './CurrencyFormatting';
import Icon from '../../app/common/components/Icon';
import ICONS from '../../app/common/constants/icons';
import Spinner from '../../app/common/components/Spinner';
import HeadTitle from '../../app/common/components/HeadTitle';

const RewardsBannerWrapper = styled.div`
    display: flex;
    flex-direction: column;
`;

export const RewardsBase = styled.div`
    padding: 12px;
    margin-bottom: 32px;
    border-radius: 4px;
    position: relative;
    box-shadow: ${SHADOWS.level1};
    background-color: ${COLORS.white};
`;

const SpinnerWrapper = styled.div`
    display: flex;
    min-height: 100%;
    justify-content: center;
    align-items: center;
`;

interface IRewardsClubProps {
    getRewardsBalances: Function;
    rewardsBalance: IRewardsBalanceState;
    numberOfDaysForExpiryAlert: number;
}

const LOCAL_STORAGE_CURRENCY = 'CURRENCY';

const selectNumberOfDaysForExpiryAlert = (state: IReduxRootState) => state.app.appConfig;
export const selectAppConfig = (state: IReduxRootState) => state.app;

export const RewardsClub: React.FC<IRewardsClubProps> = () => {
    const appConfig = useSelector((state: IReduxRootState) => state.app.appConfig);
    const dispatch = useDispatch();

    // Initialize the state when the component mounts
    React.useEffect(() => {
        dispatch(rewardsBalanceActions.initializePageState());
    }, []);

    const [localCurrency, setLocalCurrency] = React.useState<string>('');
    React.useEffect(() => {
        const currency = localStorage.getItem(LOCAL_STORAGE_CURRENCY);
        if (currency && Object.values(appConfig.CURRENCIES).includes(currency)) {
            setLocalCurrency(currency);
        } else if (window.location.host === appConfig.DOMAINS.EU) {
            localStorage.setItem(LOCAL_STORAGE_CURRENCY, appConfig.CURRENCIES.EUR);
            setLocalCurrency(appConfig.CURRENCIES.EUR);
        } else {
            localStorage.setItem(LOCAL_STORAGE_CURRENCY, clientConfigCurrency);
            setLocalCurrency(clientConfigCurrency);
        }
    }, []);

    const selectRewardsBalance = (state: IReduxRootState) => state.rewardsBalance;
    const { allRewards: rewardsList } = useSelector(selectRewardsBalance);

    const member = (state: IReduxRootState) => state.member;
    const { superPointsBalance } = useSelector(member);

    const appState = useSelector(selectAppConfig);
    const memberLoggedIn = appState?.memberLoggedIn || false;

    // To check if there is any error on the page because of any API fails or not
    const { error } = useSelector(selectRewardsBalance);

    // This will check if we have superPointBalance available from the /GBP profile endpoint and page should not have any error
    const isSuperPointsAvailable =
        superPointsBalance !== undefined && superPointsBalance !== -1 && !error;

    const {
        presentationConfig: {
            showSections: { cashbackAndPoints: showCashbackAndPoints },
        },
        locale: selectedLocale,
        clientConfig: { currency: clientConfigCurrency },
    } = useSelector(selectAppConfig);

    const { NUMBER_OF_DAYS_FOR_EXPIRY_ALERT } = useSelector(selectNumberOfDaysForExpiryAlert);

    React.useEffect(() => {
        if (isSuperPointsAvailable) {
            dispatch(rewardsBalanceActions.getRewardsBalances(showCashbackAndPoints));
        }
        // eslint-disable-next-line
    }, [isSuperPointsAvailable]);

    const [rewardBalance, setRewardsBalance] = React.useState<IRewardsBalanceResponse | null>(null);
    React.useEffect(() => {
        if (localCurrency && rewardsList.length > 0 && rewardBalance === null) {
            setRewardsBalance(
                rewardsList.find((reward) => reward.currency === localCurrency) || rewardsList[0],
            );
        }
    }, [rewardsList, clientConfigCurrency, rewardBalance, localCurrency]);

    const currencyList: string[] = createCurrencyDropdownOptions(rewardsList);

    const defaultSelectedCurrency =
        rewardsList.find((reward) => reward.currency === localCurrency)?.currency ||
        currencyList[0];

    // places the client config's currency at the top of the dropdown for usability
    const reorderedCurrencyList: string[] = rewardsList.find(
        (reward) => reward.currency === localCurrency,
    )
        ? [localCurrency, ...currencyList.filter((currency) => currency !== localCurrency)]
        : currencyList;

    const onCurrencySelect = (currency: string) => {
        const selectedCurrency = rewardsList.find((reward) => reward.currency === currency) || null;
        setRewardsBalance(selectedCurrency);
        localStorage.setItem(LOCAL_STORAGE_CURRENCY, currency);
    };

    // show spinner when member data is loading
    if (!memberLoggedIn || (isSuperPointsAvailable && !rewardBalance)) {
        return (
            <SpinnerWrapper data-qa-id="spinner">
                <Spinner size={50} />
            </SpinnerWrapper>
        );
    }

    return (
        <div data-qa-id="rewards-club-page">
            <HeadTitle />
            <CurrencySelectorStyles.CurrencyBase>
                {isSuperPointsAvailable ? (
                    <React.Fragment>
                        <CurrencySelectorStyles.CurrencyLabelIconContainer>
                            <CurrencySelectorStyles.CurrencyLabel>
                                {TranslationHelpers.getFormattedMessage('points.currency')}
                            </CurrencySelectorStyles.CurrencyLabel>
                        </CurrencySelectorStyles.CurrencyLabelIconContainer>
                        <CurrencySelectorStyles.DropdownSelector>
                            <Dropdown
                                qaId="rewards-currency-selector"
                                defaultSelected={defaultSelectedCurrency}
                                items={reorderedCurrencyList}
                                onChange={(selectedItem: string) => onCurrencySelect(selectedItem)}
                            />
                        </CurrencySelectorStyles.DropdownSelector>
                    </React.Fragment>
                ) : (
                    <CurrencySelectorStyles.ErrorMessage data-qa-id="rewards-club-error-message">
                        <CurrencySelectorStyles.ErrorIcon>
                            <Icon icon={ICONS.WARNING} size={'30px'} viewBoxMax="22" />
                        </CurrencySelectorStyles.ErrorIcon>
                        {window.location.pathname === '/cashback-and-rewards'
                            ? TranslationHelpers.getFormattedMessage('cashback.gpp.error.message')
                            : TranslationHelpers.getFormattedMessage('points.gpp.error.message')}
                    </CurrencySelectorStyles.ErrorMessage>
                )}
            </CurrencySelectorStyles.CurrencyBase>
            <RewardsBase
                className="tst-pb"
                style={isSuperPointsAvailable ? {} : { opacity: 0.5 }}
                data-qa-id="rewards-base"
            >
                <RewardsBannerWrapper>
                    {showCashbackAndPoints && (
                        <RewardsBanner
                            bannerType="rewards"
                            headerText={TranslationHelpers.getFormattedMessage('points.cashback')}
                            sectionLeftText={TranslationHelpers.getFormattedMessage(
                                'points.cashback.available.balance',
                            )}
                            sectionRightText={TranslationHelpers.getFormattedMessage(
                                'points.cashback.pending.balance',
                            )}
                            amountLeftText={
                                rewardBalance && rewardBalance.currency
                                    ? rewardBalance.currency
                                    : localCurrency
                            }
                            amountRightText={
                                rewardBalance && rewardBalance.currency
                                    ? rewardBalance.currency
                                    : localCurrency
                            }
                            leftRewardsBalance={
                                !isSuperPointsAvailable || !rewardBalance
                                    ? '-.--'
                                    : formatCashbackBalanceByLocale(
                                          rewardBalance.cashbackBalance && showCashbackAndPoints
                                              ? rewardBalance.cashbackBalance.cashbackBalance
                                              : '0.00',
                                          selectedLocale,
                                      )
                            }
                            rightRewardsBalance={
                                !isSuperPointsAvailable || !rewardBalance
                                    ? '-.--'
                                    : formatCashbackBalanceByLocale(
                                          rewardBalance.cashbackBalance && showCashbackAndPoints
                                              ? rewardBalance.cashbackBalance.pendingCashbackBalance
                                              : '0.00',
                                          selectedLocale,
                                      )
                            }
                        ></RewardsBanner>
                    )}
                    <RewardsBanner
                        bannerType="points"
                        headerText={TranslationHelpers.getFormattedMessage('points.points')}
                        sectionLeftText={TranslationHelpers.getFormattedMessage(
                            'points.available.balance',
                        )}
                        sectionRightText={TranslationHelpers.getFormattedMessage(
                            'points.pending.balance',
                        )}
                        amountLeftText={TranslationHelpers.getFormattedMessage('points.points')}
                        amountRightText={TranslationHelpers.getFormattedMessage('points.points')}
                        leftRewardsBalance={
                            rewardBalance && rewardBalance.superPointsBalance
                                ? rewardBalance.superPointsBalance.toString()
                                : isSuperPointsAvailable
                                ? '0'
                                : '--'
                        }
                        rightRewardsBalance={
                            rewardBalance && rewardBalance.pendingPointsBalance
                                ? rewardBalance.pendingPointsBalance.toString()
                                : isSuperPointsAvailable
                                ? '0'
                                : '--'
                        }
                    ></RewardsBanner>
                </RewardsBannerWrapper>

                {rewardBalance && rewardBalance.termDetails && rewardBalance.termDetails.length ? (
                    <ExpiringPointsList
                        termDetails={rewardBalance.termDetails}
                        numberOfDaysForExpiryAlert={NUMBER_OF_DAYS_FOR_EXPIRY_ALERT || 15}
                    />
                ) : (
                    <NoTermDetailsFound></NoTermDetailsFound>
                )}
            </RewardsBase>
            <RewardsTransactionsHistory
                pointsCurrency={
                    rewardBalance && rewardBalance.currency ? rewardBalance.currency : ''
                }
                cashbackCurrency={
                    rewardBalance?.cashbackBalance &&
                    rewardBalance?.cashbackBalance.cashbackCurrency
                }
                isSuperPointsAvailable={isSuperPointsAvailable}
            />
        </div>
    );
};
